import React, { useEffect, useState, createContext, useContext } from 'react';
import {
    List, Datagrid, TextField, BooleanField, EmailField, DateField, NumberField,
    Edit, SimpleForm, TextInput, BooleanInput, NumberInput, DateTimeInput,
    Create, TabbedForm, FormTab, required, useRecordContext, usePermissions,
    Show, SimpleShowLayout, RichTextField, TopToolbar, CreateButton, ExportButton, ListButton,
    useTranslate, ReferenceInput, SelectInput, ReferenceField, useGetMany,
    Toolbar, SaveButton, useNotify, useRedirect, useRefresh,
    ReferenceManyField, EditButton, WrapperField, NullableBooleanInput
} from 'react-admin';
import {
    Chip, Box, Typography, Button, Dialog, DialogTitle, DialogContent,
    DialogContentText, DialogActions, TextField as MuiTextField, CircularProgress
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useNavigate } from 'react-router-dom';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { hardDeleteDemoTenant, softDeleteTenant } from '../services/demoTenantDelete';
import { canManagePlatformTenants } from './companyPermissions';

// Context to share live course counts (fetched from main API) across list rows
const CourseCountsContext = createContext<Record<number, number>>({});

const CompanyTitle = () => {
    const record = useRecordContext();
    return <span>{record ? record.name : ''}</span>;
};

const UserCountField = ({ label }: { label?: string }) => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <Chip
            label={`${record.userCount || 0} User`}
            size="small"
            color="primary"
            variant="outlined"
        />
    );
};

const CourseCountField = ({ label }: { label?: string }) => {
    const record = useRecordContext();
    const courseCounts = useContext(CourseCountsContext);
    if (!record) return null;
    const count = courseCounts[Number(record.id)] ?? record.courseCount ?? 0;
    return (
        <Chip
            label={`${count} Kurse`}
            size="small"
            color="secondary"
            variant="outlined"
        />
    );
};

// Demo-Status-Chip mit Ablauf-Farbcode (Issue #297-Folge):
//  - kein Demo: nichts anzeigen
//  - Demo, läuft erst > 7 Tage: neutraler grauer Chip mit Datum
//  - Demo, läuft in < 7 Tagen: oranger Warn-Chip
//  - Demo, abgelaufen: roter Chip
//  - Demo ohne Ablaufdatum: grauer "Demo, unbefristet"-Chip
const DemoStatusField = () => {
    const record = useRecordContext();
    if (!record || !record.isDemoTenant) return null;
    if (!record.expiresAt) {
        return <Chip icon={<ScienceIcon />} label="Demo · unbefristet" size="small" variant="outlined" />;
    }
    const now = new Date();
    const exp = new Date(record.expiresAt);
    const daysLeft = Math.floor((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) {
        return <Chip icon={<ScienceIcon />} label={`Demo · abgelaufen ${exp.toLocaleDateString('de-DE')}`} size="small" color="error" />;
    }
    if (daysLeft <= 7) {
        return <Chip icon={<ScienceIcon />} label={`Demo · noch ${daysLeft}d (${exp.toLocaleDateString('de-DE')})`} size="small" color="warning" />;
    }
    return <Chip icon={<ScienceIcon />} label={`Demo · bis ${exp.toLocaleDateString('de-DE')}`} size="small" variant="outlined" />;
};

export const LicenseStatusField = () => {
    const record = useRecordContext();
    if (!record) return null;
    if (record.deletedAt) {
        return <Chip icon={<ArchiveIcon />} label={`Archiviert · ${new Date(record.deletedAt).toLocaleDateString('de-DE')}`} size="small" />;
    }
    if (record.isDemoTenant) return null;
    if (record.licenseStatus === 'none') {
        return <Chip icon={<VpnKeyIcon />} label="Keine Lizenz" size="small" color="error" />;
    }
    if (record.licenseStatus === 'expired') {
        const ended = record.licenseExpiresAt
            ? ` ${new Date(record.licenseExpiresAt).toLocaleDateString('de-DE')}`
            : '';
        return <Chip icon={<VpnKeyIcon />} label={`Lizenz · abgelaufen${ended}`} size="small" color="error" />;
    }
    if (!record.licenseExpiresAt) {
        return <Chip icon={<VpnKeyIcon />} label="Lizenz · unbefristet" size="small" variant="outlined" />;
    }
    const exp = new Date(record.licenseExpiresAt);
    const daysLeft = Math.ceil((exp.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 30) {
        return <Chip icon={<VpnKeyIcon />} label={`Lizenz · noch ${daysLeft}d (${exp.toLocaleDateString('de-DE')})`} size="small" color="warning" />;
    }
    return <Chip icon={<VpnKeyIcon />} label={`Lizenz · bis ${exp.toLocaleDateString('de-DE')}`} size="small" variant="outlined" />;
};

const DemoHardDeleteButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const redirect = useRedirect();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [confirmName, setConfirmName] = useState('');
    const [busy, setBusy] = useState(false);

    if (!record || !record.isDemoTenant || record.isDemoTemplate) return null;

    const close = () => { setOpen(false); setConfirmName(''); };
    const handleDelete = async () => {
        setBusy(true);
        try {
            await hardDeleteDemoTenant(Number(record.id));
            notify(`Demo-Mandant „${record.name}" wurde endgültig gelöscht.`, { type: 'success' });
            close();
            redirect('list', 'companies');
            refresh();
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Löschung fehlgeschlagen', { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button color="error" startIcon={<DeleteForeverIcon />} onClick={() => setOpen(true)}>
                Demo endgültig löschen
            </Button>
            <Dialog open={open} onClose={busy ? undefined : close} fullWidth maxWidth="sm">
                <DialogTitle>Demo-Mandanten endgültig löschen?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Alle Daten des Demo-Mandanten <strong>{record.name}</strong> werden
                        unwiderruflich gelöscht: Benutzer, Kurse, Lernstände, Medien und
                        H5P-Inhalte. Die E-Mail-Adressen der Demo-Benutzer werden wieder frei.
                        Zum Bestätigen den Namen des Mandanten eingeben.
                    </DialogContentText>
                    <MuiTextField
                        autoFocus
                        fullWidth
                        label="Name des Mandanten"
                        placeholder={record.name}
                        value={confirmName}
                        onChange={e => setConfirmName(e.target.value)}
                        disabled={busy}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={close} disabled={busy}>Abbrechen</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDelete}
                        disabled={busy || confirmName.trim() !== record.name}
                        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <DeleteForeverIcon />}
                    >
                        Endgültig löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const RealTenantArchiveButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const redirect = useRedirect();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [confirmName, setConfirmName] = useState('');
    const [busy, setBusy] = useState(false);

    if (!record || record.isDemoTenant || record.isDemoTemplate || record.deletedAt) return null;

    const close = () => { setOpen(false); setConfirmName(''); };
    const handleArchive = async () => {
        setBusy(true);
        try {
            await softDeleteTenant(Number(record.id));
            notify(`Mandant „${record.name}" wurde archiviert.`, { type: 'success' });
            close();
            redirect('list', 'companies');
            refresh();
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Archivierung fehlgeschlagen', { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button color="error" startIcon={<ArchiveIcon />} onClick={() => setOpen(true)}>
                Mandant archivieren
            </Button>
            <Dialog open={open} onClose={busy ? undefined : close} fullWidth maxWidth="sm">
                <DialogTitle>Mandanten archivieren (Kündigung)?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Der Mandant <strong>{record.name}</strong> wird deaktiviert: Alle
                        Benutzer werden anonymisiert und können sich nicht mehr anmelden,
                        die E-Mail-Adressen und die Domain werden wieder frei, Lizenzen
                        werden beendet. Kurs- und Abrechnungsdaten bleiben aus gesetzlichen
                        Aufbewahrungspflichten (10 Jahre) erhalten. Dieser Schritt kann
                        nicht über die Oberfläche rückgängig gemacht werden.
                        Zum Bestätigen den Namen des Mandanten eingeben.
                    </DialogContentText>
                    <MuiTextField
                        autoFocus
                        fullWidth
                        label="Name des Mandanten"
                        placeholder={record.name}
                        value={confirmName}
                        onChange={e => setConfirmName(e.target.value)}
                        disabled={busy}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={close} disabled={busy}>Abbrechen</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleArchive}
                        disabled={busy || confirmName.trim() !== record.name}
                        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ArchiveIcon />}
                    >
                        Archivieren
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const CompanyEditToolbar = () => {
    const record = useRecordContext();
    if (record?.deletedAt) {
        return (
            <Toolbar>
                <Typography variant="body2" color="textSecondary">
                    Archiviert am {new Date(record.deletedAt).toLocaleDateString('de-DE')}, nicht mehr bearbeitbar.
                </Typography>
            </Toolbar>
        );
    }
    return (
        <Toolbar>
            <SaveButton />
            <Box sx={{ flex: 1 }} />
            <DemoHardDeleteButton />
            <RealTenantArchiveButton />
        </Toolbar>
    );
};

const CompanyLicenses = () => {
    const company = useRecordContext();
    return (
        <ReferenceManyField reference="licenses" target="company.id" label={false}>
            <Datagrid bulkActionButtons={false} rowClick={false}>
                <ReferenceField source="product" reference="products" link={false}>
                    <TextField source="name" />
                </ReferenceField>
                <DateField source="startDate" />
                <DateField source="expiresAt" emptyText="Unbefristet" />
                <BooleanField source="active" />
                {!company?.deletedAt && <EditButton resource="licenses" />}
            </Datagrid>
        </ReferenceManyField>
    );
};

export const CompanyListActions = () => {
    const navigate = useNavigate();
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const mayCreateTenants = canManagePlatformTenants(permissions);

    return (
        <TopToolbar>
            {mayCreateTenants && (
                <>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AutoFixHighIcon />}
                        onClick={() => navigate('/company-onboarding')}
                        sx={{ mr: 1 }}
                    >
                        {translate('companyOnboarding.wizardButton')}
                    </Button>
                    <CreateButton />
                </>
            )}
            <ExportButton />
        </TopToolbar>
    );
};

export const companyFilters = [
    <TextInput key="name" source="name" label="Suche" alwaysOn />,
    <NullableBooleanInput key="active" source="active" alwaysOn />,
];

export const CompanyList = () => {
    const [courseCounts, setCourseCounts] = useState<Record<number, number>>({});

    useEffect(() => {
        const mainApiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/api\/?$/, '');
        const token = localStorage.getItem('token');
        fetch(`${mainApiUrl}/api/statistics/course-counts`, {
            headers: {
                Authorization: token ? `Bearer ${token}` : '',
                Accept: 'application/json',
            },
        })
            .then(res => res.ok ? res.json() : {})
            .then(data => setCourseCounts(data))
            .catch(() => {/* silently ignore if endpoint unavailable */});
    }, []);

    return (
        <CourseCountsContext.Provider value={courseCounts}>
            <List actions={<CompanyListActions />} filters={companyFilters} sort={{ field: 'name', order: 'ASC' }}>
                {/* kein Bulk-Delete — Loeschung nur gezielt in der Edit-Ansicht */}
                <Datagrid rowClick="edit" bulkActionButtons={false}>
                    <TextField source="id" />
                    <NumberField source="customerNumber" label="resources.companies.fields.customerNumber" />
                    {/* Anzeige-Konvention (azubi:web-Integration): Alias vor Name */}
                    <TextField source="displayName" label="resources.companies.fields.name" sortBy="name" />
                    <TextField source="city" />
                    <TextField source="country" />
                    <UserCountField label="User" />
                    <CourseCountField label="Kurse" />
                    <ReferenceField source="parentCompany" reference="companies" label="Muttergesellschaft" emptyText="">
                        <TextField source="displayName" />
                    </ReferenceField>
                    <DateField source="createdAt" label="Erstellt" showTime={false} />
                    <BooleanField source="active" />
                    <WrapperField label="Lizenz / Demo">
                        <DemoStatusField />
                        <LicenseStatusField />
                    </WrapperField>
                </Datagrid>
            </List>
        </CourseCountsContext.Provider>
    );
};

export const CompanyEdit = () => {
    // Backend-Security: Company-Patch ist ohnehin auf ROLE_OPERATOR begrenzt
    // (Entity-Config). Der Demo-Tab waere also fuer Nicht-Operatoren ein
    // sichtbarer Save-Button, der mit 403 endet — UX-maessig schoener,
    // ihn gar nicht erst zu rendern.
    const { permissions } = usePermissions();
    const isOperator = canManagePlatformTenants(permissions);
    return (
    <Edit title={<CompanyTitle />} actions={<TopToolbar><ListButton /></TopToolbar>} mutationMode="pessimistic">
        <TabbedForm toolbar={<CompanyEditToolbar />}>
            <FormTab label="resources.companies.tabs.general">
                <TextInput source="id" disabled />
                <TextInput source="name" validate={required()} fullWidth />
                <TextInput
                    source="alias"
                    fullWidth
                    helperText="resources.companies.fields.aliasHelp"
                />
                <NumberInput
                    source="customerNumber"
                    helperText="resources.companies.fields.customerNumberHelp"
                />
                <TextInput source="contactPerson" fullWidth />
                <TextInput source="email" type="email" fullWidth />
                <TextInput source="phone" fullWidth />
                <TextInput source="website" type="url" fullWidth />
                <BooleanInput source="active" />
                {isOperator && (
                    <BooleanInput
                        source="publicRegistrationEnabled"
                        label="resources.companies.fields.publicRegistrationEnabled"
                        helperText="resources.companies.fields.publicRegistrationEnabledHelp"
                    />
                )}
            </FormTab>
            <FormTab label="resources.companies.tabs.address">
                <TextInput source="street" fullWidth />
                <TextInput source="addressSupplement1" fullWidth />
                <TextInput source="addressSupplement2" fullWidth />
                <TextInput source="zip" />
                <TextInput source="city" fullWidth />
                <TextInput source="country" fullWidth />
            </FormTab>
            <FormTab label="resources.companies.tabs.billing">
                <TextInput
                    source="billingEmail"
                    type="email"
                    fullWidth
                    helperText="resources.companies.fields.billingEmailHelp"
                />
                <TextInput source="billingStreet" fullWidth />
                <TextInput source="billingZip" />
                <TextInput source="billingCity" fullWidth />
                <TextInput source="billingCountry" fullWidth />
            </FormTab>
            <FormTab label="resources.companies.tabs.legal">
                <TextInput source="taxId" fullWidth />
                <TextInput source="commercialRegister" fullWidth />
            </FormTab>
            <FormTab label="resources.companies.tabs.metadata">
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" color="textSecondary">Erstellt am</Typography>
                        <DateField source="createdAt" showTime />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" color="textSecondary">Zuletzt aktualisiert</Typography>
                        <DateField source="updatedAt" showTime emptyText="Noch nicht aktualisiert" />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <NumberInput source="userCount" label="Anzahl Benutzer" disabled />
                    <NumberInput source="courseCount" label="Anzahl Kurse" disabled />
                    <NumberInput source="maxUsers" label="Max. Benutzer" helperText="Leer = unbegrenzt. Nur Operatoren können dieses Limit ändern." />
                </Box>
                <TextInput source="notes" label="Notizen" multiline rows={4} fullWidth />
            </FormTab>
            {isOperator && (
                <FormTab label="resources.companies.tabs.licenses">
                    <CompanyLicenses />
                </FormTab>
            )}
            {/* Issue #297-Folge: Demo-Status + Ablauf war bisher nur in der DB
                sichtbar (company.expires_at). Operator brauchte SSH+SQL, um
                Demo-Lizenzen zu verlaengern. Jetzt direkt im Admin editierbar
                — aber nur fuer Operatoren, weil die Demo-Flags Plattform-
                Konzept sind (Cleanup-Cron-Steuerung, eigene Subdomain,
                Demo-Banner). Backend-Security ist ohnehin Operator-only;
                Tab nur als UX-Schutz vor frustrierenden 403-Errors. */}
            {isOperator && (
                <FormTab label="resources.companies.tabs.demo">
                    <BooleanInput
                        source="isDemoTenant"
                        label="resources.companies.fields.isDemoTenant"
                        helperText="resources.companies.fields.isDemoTenantHelp"
                    />
                    <DateTimeInput
                        source="expiresAt"
                        label="resources.companies.fields.expiresAt"
                        helperText="resources.companies.fields.expiresAtHelp"
                    />
                </FormTab>
            )}
            {/* Konzern-Hierarchie (Hotelketten): Verknuepfung ist Plattform-
                Konzept wie die Demo-Flags — Backend-Security ist ohnehin
                Operator-only, Tab nur fuer Operatoren rendern. */}
            {isOperator && (
                <FormTab label="resources.companies.tabs.group">
                    <ParentCompanyInput />
                    <SubsidiariesField />
                </FormTab>
            )}
        </TabbedForm>
    </Edit>
    );
};

// Selbstbezug und tiefere Zyklen fängt die Backend-Validierung
// (Company::validateParentCompany) — hier bewusst keine Choice-Filterung.
const ParentCompanyInput = () => (
    <ReferenceInput source="parentCompany" reference="companies" perPage={200} sort={{ field: 'name', order: 'ASC' }}>
        <SelectInput
            label="resources.companies.fields.parentCompany"
            helperText="resources.companies.fields.parentCompanyHelp"
            optionText="name"
            emptyText="– Keine Muttergesellschaft –"
            fullWidth
        />
    </ReferenceInput>
);

const SubsidiariesField = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const subsidiaryIds = (record && Array.isArray(record.subsidiaries)) ? record.subsidiaries : [];
    const { data } = useGetMany(
        'companies',
        { ids: subsidiaryIds },
        { enabled: subsidiaryIds.length > 0 }
    );
    if (subsidiaryIds.length === 0) {
        return null;
    }
    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                {translate('resources.companies.fields.subsidiaries')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {(data || []).map((subsidiary: any) => (
                    <Chip key={subsidiary.id} label={subsidiary.name} size="small" variant="outlined" />
                ))}
            </Box>
        </Box>
    );
};

export const CompanyCreate = () => (
    <Create actions={<TopToolbar><ListButton /></TopToolbar>}>
        <TabbedForm>
            <FormTab label="resources.companies.tabs.general">
                <TextInput source="name" validate={required()} fullWidth />
                <TextInput
                    source="alias"
                    fullWidth
                    helperText="resources.companies.fields.aliasHelp"
                />
                <NumberInput
                    source="customerNumber"
                    helperText="resources.companies.fields.customerNumberHelp"
                />
                <TextInput source="contactPerson" fullWidth />
                <TextInput source="email" type="email" fullWidth />
                <TextInput source="phone" fullWidth />
                <TextInput source="website" type="url" fullWidth />
                <BooleanInput source="active" defaultValue={true} />
            </FormTab>
            <FormTab label="resources.companies.tabs.address">
                <TextInput source="street" fullWidth />
                <TextInput source="addressSupplement1" fullWidth />
                <TextInput source="addressSupplement2" fullWidth />
                <TextInput source="zip" />
                <TextInput source="city" fullWidth />
                <TextInput source="country" fullWidth />
            </FormTab>
            <FormTab label="resources.companies.tabs.billing">
                <TextInput
                    source="billingEmail"
                    type="email"
                    fullWidth
                    helperText="resources.companies.fields.billingEmailHelp"
                />
                <TextInput source="billingStreet" fullWidth />
                <TextInput source="billingZip" />
                <TextInput source="billingCity" fullWidth />
                <TextInput source="billingCountry" fullWidth />
            </FormTab>
            <FormTab label="resources.companies.tabs.legal">
                <TextInput source="taxId" fullWidth />
                <TextInput source="commercialRegister" fullWidth />
            </FormTab>
            <FormTab label="resources.companies.tabs.group">
                <ParentCompanyInput />
            </FormTab>
        </TabbedForm>
    </Create>
);
